import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { BillingPlanIds } from '../../../../../enums/billing-plan-ids';

import { useEnableSimplifiedPlanGrid } from '../../../../../feature-flags/hooks/use-enable-simplified-plan-grid';
import { useIsSellerOnFeatureGatedPlanProfile } from '../../../../../feature-gates/use-is-seller-on-feature-gated-plan-profile';
import Global from '../../../../../store/global-selectors';
import { useManageSubscriptionDrawerContext } from '../../context/manage-subscription-drawer-context';

export const PlanGridConfig = {
  GbbLineup: 'gbb-lineup',
  SimplifiedLegacy: 'simplified-legacy',
  StandardLegacy: 'standard-legacy',
} as const;

export type PlanGridConfigType = (typeof PlanGridConfig)[keyof typeof PlanGridConfig];

type SectionVisibility = {
  planGridConfig: PlanGridConfigType;
  canMigrate: boolean;
  shouldForceNewestPlans: boolean;
};

export const useManageSubscriptionDrawerSectionVisibility = (): SectionVisibility => {
  const isNewLineupPlanGridEnabled = useIsSellerOnFeatureGatedPlanProfile();
  const isSimplifiedPlanGridEnabled = useEnableSimplifiedPlanGrid();
  const { canMigrate, isMigratingToNewestPlans, shouldForceNewestPlans = false } = useManageSubscriptionDrawerContext();
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);

  const { currentBillingPlan } = subscriptionData ?? {};
  const currentBillingPlanId = currentBillingPlan?.billingPlanId;

  return useMemo(() => {
    const isTrialSeller = currentBillingPlanId === BillingPlanIds.Trial.toString();

    const shouldShowNewLineupPlanGrid = isNewLineupPlanGridEnabled || isMigratingToNewestPlans || shouldForceNewestPlans || isTrialSeller;
    const shouldShowSimplifiedPlanGrid = isSimplifiedPlanGridEnabled;

    let planGridConfig: PlanGridConfigType;
    if (shouldShowNewLineupPlanGrid) {
      planGridConfig = PlanGridConfig.GbbLineup;
    } else if (shouldShowSimplifiedPlanGrid) {
      planGridConfig = PlanGridConfig.SimplifiedLegacy;
    } else {
      planGridConfig = PlanGridConfig.StandardLegacy;
    }

    return {
      planGridConfig,
      canMigrate,
      shouldForceNewestPlans,
    };
  }, [
    isNewLineupPlanGridEnabled,
    isSimplifiedPlanGridEnabled,
    canMigrate,
    shouldForceNewestPlans,
    isMigratingToNewestPlans,
    currentBillingPlanId,
  ]);
};
